//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';

import Cuid from '@models/Cuid';
import PartialCuid from '@models/PartialCuid';
import { getClientId } from '@redux/selectors/getClientId';
import { useMediaTrackingContext } from '@tracker/mediaTracking/contexts/MediaTrackingContext';
import { MediaTrackingEventName } from '@tracker/mediaTracking/mediaTrackingModel';
import { useMarkRightsApprovedMutation } from '@uia/details/hooks/useMarkRightsApprovedMutation';
import { UIA_RIGHTS_FLOW_TRACKING_IDS } from '@uia/rights/constants/uiaRightsConstants';
import { UIARightsSetExpiry } from '@uia/rights/rights-expiry/UIARightsSetExpiry';
import { getIsBazaarvoiceNetworkSyndicationFrontendEnabled } from '@uia/rights/selectors/getUIARights';

interface UIARightsMarkApprovedFormProps {
  expiryTime?: Date;
  handleReturnToRightsOptions: () => void;
  handleSubmit?: () => void;
  onUpdateRights: () => void;
  partialCuid: PartialCuid;
}

export const UIARightsMarkApprovedForm = ({
  expiryTime,
  handleReturnToRightsOptions,
  onUpdateRights,
  partialCuid,
}: UIARightsMarkApprovedFormProps) => {
  const clientId = useSelector(getClientId);
  const isBazaarvoiceNetworkSyndicationFrontendEnabled = useSelector(
    getIsBazaarvoiceNetworkSyndicationFrontendEnabled,
  );

  const { isPending: isMarkAsApprovedLoading, mutate: markAsApproved } =
    useMarkRightsApprovedMutation();
  const { trackEvent } = useMediaTrackingContext();

  const handleOnSubmit = (expiryDate?: Date) => {
    trackEvent({
      cuid: Cuid.fromPartial(partialCuid, clientId),
      event: MediaTrackingEventName.RightsManuallyApproved,
    });
    markAsApproved(
      {
        clientId,
        cuid: partialCuid,
        expirationTime: expiryDate?.getTime(),
      },
      { onSuccess: onUpdateRights },
    );
  };

  return (
    <UIARightsSetExpiry
      alertText={
        isBazaarvoiceNetworkSyndicationFrontendEnabled
          ? 'Content rights must be requested to be eligible for distribution to the Visual Syndication Network.'
          : undefined
      }
      expiryTime={expiryTime}
      handleOnPreviousClick={handleReturnToRightsOptions}
      handleSubmit={handleOnSubmit}
      infoText='This image will be used without requesting permission.'
      isExpiryDateOptional={true}
      isSubmitInProgress={isMarkAsApprovedLoading}
      previousButtonTrackingId={
        UIA_RIGHTS_FLOW_TRACKING_IDS.MARK_APPROVED_PREVIOUS_BUTTON
      }
      submitButtonText='Approve'
      submitButtonTrackingId={
        UIA_RIGHTS_FLOW_TRACKING_IDS.MARK_APPROVED_SUBMIT_BUTTON
      }
      title='Mark as approved'
    />
  );
};
